"use client";

import { useAuth, useOrganization, useUser } from "@clerk/nextjs";
import { datadogRum } from "@datadog/browser-rum";
import { useEffect } from "react";

declare global {
  interface Window {
    __agentOpsDatadogRumInitialized?: boolean;
  }
}

function numberEnv(value: string | undefined, fallback: number) {
  if (value == null || value.trim() === "") return fallback;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function defaultSessionReplaySampleRate(env: string | undefined) {
  return env === "production" ? 0 : 100;
}

function listEnv(value: string | undefined) {
  return (value || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function allowedTracingUrls() {
  return Array.from(new Set([
    window.location.origin,
    ...listEnv(process.env.NEXT_PUBLIC_DD_RUM_ALLOWED_TRACING_URLS),
  ]));
}

export function DatadogRum() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { orgId, orgRole } = useAuth();
  const { organization } = useOrganization();

  useEffect(() => {
    const applicationId = process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID;
    const clientToken = process.env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN;
    if (!applicationId || !clientToken) return;
    if (window.__agentOpsDatadogRumInitialized) return;
    const env = process.env.NEXT_PUBLIC_DD_RUM_ENV || process.env.NEXT_PUBLIC_DD_ENV || process.env.NODE_ENV;

    datadogRum.init({
      applicationId,
      clientToken,
      site: process.env.NEXT_PUBLIC_DD_RUM_SITE || process.env.NEXT_PUBLIC_DD_SITE || "datadoghq.com",
      service: process.env.NEXT_PUBLIC_DD_RUM_SERVICE || "scout",
      env,
      version: process.env.NEXT_PUBLIC_DD_VERSION,
      sessionSampleRate: numberEnv(process.env.NEXT_PUBLIC_DD_RUM_SESSION_SAMPLE_RATE, 100),
      sessionReplaySampleRate: numberEnv(
        process.env.NEXT_PUBLIC_DD_RUM_SESSION_REPLAY_SAMPLE_RATE,
        defaultSessionReplaySampleRate(env),
      ),
      allowedTracingUrls: allowedTracingUrls(),
      propagateTraceBaggage: true,
      traceSampleRate: numberEnv(process.env.NEXT_PUBLIC_DD_RUM_TRACE_SAMPLE_RATE, 100),
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
    });
    window.__agentOpsDatadogRumInitialized = true;
  }, []);

  useEffect(() => {
    if (!window.__agentOpsDatadogRumInitialized || !isLoaded) return;

    if (!isSignedIn || !user) {
      datadogRum.clearUser();
      datadogRum.clearAccount();
      return;
    }

    datadogRum.setUser({
      id: user.id,
      name: user.fullName || user.username || user.primaryEmailAddress?.emailAddress || user.id,
      email: user.primaryEmailAddress?.emailAddress,
    });

    if (orgId) {
      datadogRum.setAccount({
        id: orgId,
        name: organization?.name || orgId,
        role: orgRole || undefined,
      });
    } else {
      datadogRum.clearAccount();
    }
  }, [
    isLoaded,
    isSignedIn,
    orgId,
    orgRole,
    organization?.name,
    user?.fullName,
    user?.id,
    user?.primaryEmailAddress?.emailAddress,
    user?.username,
    user,
  ]);

  return null;
}
